export interface BlogGroupEntry {
  group: string
  displayTitle: string
  learningOutcome: string
  order: number
}

export type CurriculumKey = 'claude-code' | 'ai-agents' | 'ai-coding-tools'

export interface CurriculumSection {
  key: CurriculumKey
  label: string
  matchTags: string[]
  icon: string
  blurb: string
}

export const curriculumSections: CurriculumSection[] = [
  {
    key: 'claude-code',
    label: 'Claude Code Fundamentals',
    matchTags: ['claude-code', 'claude-md'],
    icon: '/images/claude-icon.png',
    blurb: "Anthropic's terminal coding agent, end to end",
  },
  {
    key: 'ai-agents',
    label: 'Build AI Agents',
    matchTags: ['ai-agents', 'agents', 'mcp'],
    icon: 'bot',
    blurb: 'From first principles to production',
  },
  {
    key: 'ai-coding-tools',
    label: 'AI Coding Tools',
    matchTags: ['cursor', 'ai-coding', 'ide', 'google-antigravity'],
    icon: 'zap',
    blurb: 'Cursor, IDEs & the rest of the stack',
  },
]

export const curriculumGroupOrder: Record<CurriculumKey, string[]> = {
  'claude-code': ['Getting Started', 'Workflows & Techniques'],
  'ai-agents': ['Fundamentals', 'Build From Scratch', 'Tools & Infrastructure', 'Production'],
  'ai-coding-tools': ['Cursor & Tools'],
}

// ── Curriculum: only sequential learning content ────────────────────
export const blogGroupMap: Record<string, BlogGroupEntry> = {
  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  CLAUDE CODE > Getting Started                                  ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'claude-code': {
    group: 'Getting Started',
    displayTitle: 'Everything You Need to Ship with Claude Code',
    learningOutcome: 'The complete Claude Code reference - install, configure, master.',
    order: 1,
  },
  'claude-code-tutorial-beginners': {
    group: 'Getting Started',
    displayTitle: 'Install, Configure, and Ship Your First Feature',
    learningOutcome: 'Install Claude Code, run your first terminal session, understand the basics.',
    order: 2,
  },
  'claude-md-configuration-guide': {
    group: 'Getting Started',
    displayTitle: 'The CLAUDE.md Guide: Configure for Any Project',
    learningOutcome: 'Project config, memory, custom instructions with CLAUDE.md.',
    order: 3,
  },
  'karpathy-claude-md-rules': {
    group: 'Getting Started',
    displayTitle: "Karpathy's CLAUDE.md Rules",
    learningOutcome: "Karpathy's real CLAUDE.md config - what he actually uses.",
    order: 4,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  CLAUDE CODE > Workflows & Techniques                           ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'claude-code-best-practices-prompts': {
    group: 'Workflows & Techniques',
    displayTitle: '10 Prompting Patterns That Actually Work',
    learningOutcome: 'Prompting patterns that consistently get better results.',
    order: 10,
  },
  'claude-code-hooks-complete-guide': {
    group: 'Workflows & Techniques',
    displayTitle: 'Agent Hooks: Rules Your AI Can\'t Ignore',
    learningOutcome: 'Lifecycle events, matchers, and 7 copy-paste hook recipes.',
    order: 11,
  },
  'claude-code-sub-agents-guide': {
    group: 'Workflows & Techniques',
    displayTitle: 'SubAgent: Context Isolation',
    learningOutcome: '3x output with parallel task delegation and clean context.',
    order: 12,
  },
  'claude-code-agent-teams-guide': {
    group: 'Workflows & Techniques',
    displayTitle: 'Agent Teams: Run an AI Project Group',
    learningOutcome: 'Team lead, teammates, shared tasks, and direct messaging.',
    order: 14,
  },
  'orchestrator-worker-sidekick-paradigm': {
    group: 'Workflows & Techniques',
    displayTitle: 'The Sidekick Paradigm: Orchestrator-Worker Agents',
    learningOutcome: 'Strong model plans, cheap model executes - cut AI coding cost ~80% without losing quality.',
    order: 15,
  },
  'claude-code-dynamic-workflows': {
    group: 'Workflows & Techniques',
    displayTitle: 'Dynamic Workflows: Orchestrate Subagents at Scale',
    learningOutcome: 'Move the loop into a script - run up to 1,000 subagents without flooding context.',
    order: 15.5,
  },
  'claude-code-worktree-parallel-agents': {
    group: 'Workflows & Techniques',
    displayTitle: 'Worktree: File Isolation for Parallel Agents',
    learningOutcome: 'Give every agent its own directory - no more write conflicts.',
    order: 16,
  },
  'claude-code-todowrite-vs-task': {
    group: 'Workflows & Techniques',
    displayTitle: 'TodoWrite vs Task: Agent Task Management',
    learningOutcome: 'Session checklists vs persistent task graphs - when to use which.',
    order: 17,
  },
  'karpathy-parallel-agents-workflow': {
    group: 'Workflows & Techniques',
    displayTitle: 'Run Parallel Sessions Like Karpathy',
    learningOutcome: 'Run parallel Claude Code sessions for maximum throughput.',
    order: 19,
  },
  'reduce-claude-code-api-costs': {
    group: 'Workflows & Techniques',
    displayTitle: 'Cut API Costs 73% (7 Strategies)',
    learningOutcome: '7 strategies to cut API costs without sacrificing quality.',
    order: 20,
  },
  'claude-code-plugins-official-library-2026': {
    group: 'Workflows & Techniques',
    displayTitle: 'Official Plugin Library + SKILL.md',
    learningOutcome: "Official Anthropic plugin library and SKILL.md auto-loading.",
    order: 21,
  },
  'claude-code-auto-load-skills-v2157': {
    group: 'Workflows & Techniques',
    displayTitle: 'SKILL.md Auto-Loading (No Marketplace)',
    learningOutcome: 'Drop a SKILL.md and Claude Code auto-loads it.',
    order: 22,
  },
  'agent-skills-best-practices-guide': {
    group: 'Workflows & Techniques',
    displayTitle: "Anthropic's 300+ Skills Playbook",
    learningOutcome: "Anthropic's internal playbook for building reliable Skills.",
    order: 23,
  },
  'codex-skills-sam-altman-framework-2026': {
    group: 'Workflows & Techniques',
    displayTitle: "Sam Altman's Skills Framework for Codex",
    learningOutcome: "Most agent failures are context problems, not model problems.",
    order: 24,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  AI AGENTS > Fundamentals                                       ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'ai-agents': {
    group: 'Fundamentals',
    displayTitle: 'AI Agents: The Complete 2026 Roadmap',
    learningOutcome: 'Complete guide to building, deploying, and scaling AI agents.',
    order: 1,
  },
  'ai-agents-101-part-1': {
    group: 'Fundamentals',
    displayTitle: 'What Is an AI Agent?',
    learningOutcome: 'What an AI agent is - planning, memory, tools, action.',
    order: 2,
  },
  'function-calling-how-llms-use-tools': {
    group: 'Fundamentals',
    displayTitle: 'Function Calling: How LLMs Use Tools',
    learningOutcome: 'The menu-order-serve mechanism behind every agent action.',
    order: 3,
  },
  'ai-agents-101-part-2': {
    group: 'Fundamentals',
    displayTitle: 'Give Your Agent Tools (Function Calling)',
    learningOutcome: 'Give your agent real tools - function calling and MCP.',
    order: 5,
  },
  'ai-agents-101-part-3': {
    group: 'Fundamentals',
    displayTitle: 'Agent Memory: Short-term and Long-term',
    learningOutcome: 'Agent memory - short-term, long-term, and retrieval.',
    order: 6,
  },
  'agent-memory-systems-guide': {
    group: 'Fundamentals',
    displayTitle: 'Memory Systems: Episodic, Semantic, Procedural',
    learningOutcome: 'The 3 memory types, storage layers, and MemGPT vs Mem0 vs Claude Code.',
    order: 7,
  },
  'ai-agents-101-part-4': {
    group: 'Fundamentals',
    displayTitle: 'Multi-Agent Systems: Orchestration',
    learningOutcome: 'Multi-agent systems - orchestration and delegation.',
    order: 9,
  },
  'ai-agents-101-part-5': {
    group: 'Fundamentals',
    displayTitle: 'Deploy Agents to Production',
    learningOutcome: 'Deploy agents to production - reliability and monitoring.',
    order: 10,
  },
  'karpathy-agentic-engineering': {
    group: 'Fundamentals',
    displayTitle: "Karpathy's Agentic Engineering Framework",
    learningOutcome: "Karpathy's framework for serious AI builders.",
    order: 11,
  },
  'karpathy-agents-md-framework': {
    group: 'Fundamentals',
    displayTitle: "Karpathy's agents.md: 4 Rules",
    learningOutcome: "What Karpathy's agents.md would look like - four rules for production agents.",
    order: 12,
  },
  'karpathy-software-3-0': {
    group: 'Fundamentals',
    displayTitle: 'Software 3.0: Context Window = Program',
    learningOutcome: 'The paradigm shift from traditional software to AI-native code.',
    order: 13,
  },
  'karpathy-llm-wiki': {
    group: 'Fundamentals',
    displayTitle: "Karpathy's LLM Wiki Pattern",
    learningOutcome: "Build a personal knowledge base that compounds.",
    order: 14,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  AI AGENTS > Build From Scratch                                 ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'how-to-build-ai-agent-from-scratch': {
    group: 'Build From Scratch',
    displayTitle: 'Build an Agent in 60 Lines of Python',
    learningOutcome: 'Build an AI agent from scratch in Python - step by step.',
    order: 20,
  },
  'multi-agent-system-python-tutorial': {
    group: 'Build From Scratch',
    displayTitle: 'Multi-Agent Coordinator + Workers (200 lines)',
    learningOutcome: 'Multi-agent coordination in Python - real working code.',
    order: 21,
  },
  'hermes-nous-research-self-improving-agent': {
    group: 'Build From Scratch',
    displayTitle: 'Hermes: Self-Hosted, Never Forgets',
    learningOutcome: 'Self-hosted AI agent that retains memory across sessions.',
    order: 22,
  },
  'gemma4-local-agents': {
    group: 'Build From Scratch',
    displayTitle: 'Gemma 4: Free Local Agents with Ollama',
    learningOutcome: 'Run free agentic AI locally with Ollama + Gemma 4.',
    order: 23,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  AI AGENTS > Tools & Infrastructure                             ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'mcp-101-build-mcp-servers': {
    group: 'Tools & Infrastructure',
    displayTitle: 'MCP 101: Build Your First Server',
    learningOutcome: 'Build your first MCP server from zero.',
    order: 30,
  },
  'mcp-internals-client-server': {
    group: 'Tools & Infrastructure',
    displayTitle: 'MCP Internals: Client-Server Communication',
    learningOutcome: 'STDIO vs SSE, JSON-RPC, and the full 6-step interaction loop.',
    order: 31,
  },
  'mcp-security-attack-vectors': {
    group: 'Tools & Infrastructure',
    displayTitle: 'MCP Security: 6 Attack Vectors',
    learningOutcome: 'Tool poisoning, exfiltration, rug pulls - and the 5-step audit.',
    order: 32,
  },
  'context-engineering-guide': {
    group: 'Tools & Infrastructure',
    displayTitle: 'Context Engineering: The Real Bottleneck',
    learningOutcome: '4 strategies, 4 failure modes, and KV-cache economics.',
    order: 36,
  },
  'rag-vs-long-context-vs-fine-tuning': {
    group: 'Tools & Infrastructure',
    displayTitle: 'RAG vs Long Context vs Fine-Tuning',
    learningOutcome: 'When each wins, the modern pipeline, and agentic retrieval.',
    order: 37,
  },
  'ai-coding-agent-memory-agentmemory': {
    group: 'Tools & Infrastructure',
    displayTitle: 'agentmemory: Fix Memory Loss (30 sec)',
    learningOutcome: 'Fix agent memory loss in 30 seconds with agentmemory.',
    order: 38,
  },
  'prompt-engineering-guide-2026': {
    group: 'Tools & Infrastructure',
    displayTitle: 'Prompt Engineering Techniques (2026)',
    learningOutcome: 'The prompting techniques that actually move the needle in 2026.',
    order: 39,
  },
  'markitdown-microsoft-convert-files-markdown-llm': {
    group: 'Tools & Infrastructure',
    displayTitle: 'MarkItDown: PDF to Markdown for RAG',
    learningOutcome: 'Convert PDFs, DOCX, HTML to clean Markdown for RAG pipelines.',
    order: 40,
  },
  'webmcp-complete-guide': {
    group: 'Tools & Infrastructure',
    displayTitle: 'WebMCP: Agents on Websites',
    learningOutcome: 'How websites expose structured tools to in-browser agents.',
    order: 34,
  },
  'google-skills-official-agent-skills-library': {
    group: 'Tools & Infrastructure',
    displayTitle: "google/skills: Official Agent Skills Library",
    learningOutcome: "Google's official Agent Skills for BigQuery, Cloud Run, GKE, Firebase.",
    order: 41,
  },
  'last30days-skill-real-time-research': {
    group: 'Tools & Infrastructure',
    displayTitle: 'last30days: Real-Time Research Skill',
    learningOutcome: 'Real-time research across Reddit, X, YouTube, HN.',
    order: 42,
  },
  'codebase-memory-mcp-guide': {
    group: 'Tools & Infrastructure',
    displayTitle: 'Codebase Memory MCP: A Map for Agents',
    learningOutcome: 'Turn your repo into a code graph - half the tokens per task.',
    order: 43,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  AI AGENTS > Production                                         ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'agent-modes-plan-default-auto': {
    group: 'Production',
    displayTitle: 'Agent Modes: Plan vs Default vs Auto',
    learningOutcome: 'Three trust levels and the gear-shifting workflow between them.',
    order: 50,
  },
  'agent-sandbox-os-level-security': {
    group: 'Production',
    displayTitle: 'Agent Sandbox: OS-Level Security',
    learningOutcome: 'Filesystem + network walls: Seatbelt, bwrap, gVisor, Firecracker.',
    order: 51,
  },
  'prompt-context-harness-evolution': {
    group: 'Production',
    displayTitle: 'From Prompts to Harness: 3 Shifts',
    learningOutcome: 'Why AI engineering moved from phrasing to information to control.',
    order: 52,
  },
  'harness-six-components': {
    group: 'Production',
    displayTitle: 'Harness: The 6 Components',
    learningOutcome: 'Context, tools, orchestration, state, evaluation, recovery.',
    order: 53,
  },
  'pi-agent-extensions-guide': {
    group: 'Production',
    displayTitle: 'Pi Extensions: Rewrite the Harness',
    learningOutcome: 'In-process extensions vs out-of-process hooks - what each can actually reach, and where the real ceiling is.',
    order: 53.5,
  },
  'harness-engineering-agent-production-guide': {
    group: 'Production',
    displayTitle: 'Harness in Practice: OpenAI + Anthropic',
    learningOutcome: 'What OpenAI and Anthropic changed for production agent systems.',
    order: 54,
  },
  'yc-qm-agent-harness-source-read': {
    group: 'Production',
    displayTitle: "YC's QM: A Source-Code Read",
    learningOutcome: 'Twice as much of YC\'s open-source harness governs scope as drives the model - the memory notebook, the audience-filtered transcript, the egress proxy, and the limits its own SECURITY.md names.',
    order: 54.5,
  },
  'loop-engineering-guide-2026': {
    group: 'Production',
    displayTitle: 'Loop Engineering: Write Verifiers, Not Prompts',
    learningOutcome: 'Why the verifier is the bottleneck, open vs closed loops, and how to make either ship.',
    order: 54.5,
  },
  'loop-engineering-anthropic-playbook': {
    group: 'Production',
    displayTitle: 'Loop Engineering: The Anthropic Playbook',
    learningOutcome: "Anthropic's gather-act-verify loop, and the five principles that make it reliable.",
    order: 54.6,
  },
  'loop-engineering-karpathy': {
    group: 'Production',
    displayTitle: 'Loop Engineering, Karpathy-Style',
    learningOutcome: "How Karpathy's generation-verification loop and autonomy slider became loop engineering.",
    order: 54.65,
  },
  'loops-md-karpathy': {
    group: 'Production',
    displayTitle: "Karpathy's LOOPS.md: What It Says and What's Verified",
    learningOutcome: 'What the circulating LOOPS.md file claims, why the rules are real loop engineering, and what we could verify about the attribution.',
    order: 54.67,
  },
  'types-of-agentic-loops': {
    group: 'Production',
    displayTitle: 'The 4 Types of Agentic Loops',
    learningOutcome: 'Turn-based, goal-based, time-based, and proactive loops - which part of the cycle each hands off.',
    order: 54.7,
  },
  'loop-engineering-addy-osmani': {
    group: 'Production',
    displayTitle: "Addy Osmani's Loop Engineering: The 5 Components",
    learningOutcome: "Osmani's five loop components, the three debts, and why you still own the outer loop.",
    order: 54.8,
  },
  'loop-engineering-case-study': {
    group: 'Production',
    displayTitle: 'Loop Engineering in Practice: 30 Days of Data',
    learningOutcome: 'A real two-loop content system with every number published - the run log, the quality-valve skips, and the failures.',
    order: 54.9,
  },
  'self-improving-agent-loops': {
    group: 'Production',
    displayTitle: 'The Evolve Loop: Loops That Rewrite Themselves',
    learningOutcome: 'How an evolve run rewrites a loop\'s own contract, state, and triggers - and the rule that stops it becoming busywork.',
    order: 54.85,
  },
  'control-graph-llm-as-graph-vs-code-as-graph': {
    group: 'Production',
    displayTitle: 'Graph Engineering From the Inside: Control Graphs',
    learningOutcome: 'The three things "graph engineering" means, the two ways to enforce a control graph (code vs a skill), four patterns for cutting nodes, and two production graphs.',
    order: 54.905,
  },
  'graph-engineering-guide-2026': {
    group: 'Production',
    displayTitle: 'Graph Engineering: Wire Loops into a Graph',
    learningOutcome: 'When one agent loop is not enough: nodes, edges, shared state, the frameworks, and when NOT to reach for a graph.',
    order: 54.91,
  },
  'graph-engineering-vs-loop-engineering': {
    group: 'Production',
    displayTitle: 'Graph vs Loop Engineering: What Changed',
    learningOutcome: 'What genuinely changed versus what is a rebrand - a before/after build and a paradigm gut-check.',
    order: 54.92,
  },
  'compound-engineering-vs-loop-engineering': {
    group: 'Production',
    displayTitle: 'Compound vs Loop Engineering',
    learningOutcome: "What separates Every's compound engineering (units of work that teach the next one) from loop engineering (operations that run themselves), and a 3-question sort for which jobs fit which frame.",
    order: 54.925,
  },
  'agent-graph-vs-loop-when-to-use': {
    group: 'Production',
    displayTitle: 'Graph or Loop: How to Decide',
    learningOutcome: 'The 5 signals you have outgrown a loop, the cost of a graph you did not need, and a 30-second decision tree.',
    order: 54.93,
  },
  'is-graph-engineering-just-langgraph': {
    group: 'Production',
    displayTitle: 'Is It Just LangGraph? Frameworks Compared',
    learningOutcome: 'LangGraph vs AutoGen GraphFlow vs Google ADK vs A2A - the pattern versus the implementations.',
    order: 54.94,
  },
  'five-layers-ai-engineering': {
    group: 'Production',
    displayTitle: 'The 5 Layers of AI Engineering',
    learningOutcome: 'Prompt, context, harness, loop, graph - the whole stack, and which layer is your bottleneck right now.',
    order: 54.95,
  },
  'andrew-ng-loop-to-graph-engineering': {
    group: 'Production',
    displayTitle: "Andrew Ng's Agentic Patterns, Mapped to Graphs",
    learningOutcome: "What Ng published, what the viral PDF posts claim, and where AI Builder Club's graph mapping begins.",
    order: 54.98,
  },
  'graph-engineering-karpathy-loop': {
    group: 'Production',
    displayTitle: 'Graph Engineering and the Karpathy Loop',
    learningOutcome: "What Karpathy's autoresearch loop actually does, why the linked 11-page PDF is real but not an Anthropic publication, and which of the six steps are worth climbing.",
    order: 54.99,
  },
  'google-graph-engineering': {
    group: 'Production',
    displayTitle: 'Google Graph Engineering: Claim vs Reality',
    learningOutcome: "Why the viral free Google course does not exist, the ADK 2.0 graph workflows that do, and the free Google material worth your time.",
    order: 54.995,
  },
  'graph-engineering-with-claude-code': {
    group: 'Production',
    displayTitle: 'Graph Engineering with Claude Code',
    learningOutcome: 'Subagents as nodes, the orchestrator as edges, the Agent SDK - wire an agent graph with the tool you already have open.',
    order: 54.96,
  },
  'graph-engineering-peter-steinberger': {
    group: 'Production',
    displayTitle: 'Peter Steinberger\'s "Loops or Graphs?" Tweet',
    learningOutcome: 'Trace the first four days of the July 2026 wave while separating Steinberger\'s amplification from the phrase\'s earlier history.',
    order: 54.97,
  },
  'how-to-evaluate-ai-agents': {
    group: 'Production',
    displayTitle: 'How to Evaluate AI Agents',
    learningOutcome: 'Generator-evaluator, traces, eval sets, and LLM-as-judge pitfalls.',
    order: 55,
  },
  'open-source-ai-company-multi-agent': {
    group: 'Production',
    displayTitle: 'Proactive Agents & the Company-as-Code Pattern',
    learningOutcome: 'What a 147-agent "AI company" repo teaches about proactive, self-improving systems.',
    order: 57,
  },
  'claude-fable-5-how-to-use-guide': {
    group: 'Production',
    displayTitle: 'Claude Fable 5: Access, Pricing, Prompting',
    learningOutcome: 'How to use Claude Fable 5 - access, prompting, pricing, refusals.',
    order: 58,
  },
  'how-to-become-an-ai-native-company': {
    group: 'Production',
    displayTitle: 'How to Become an AI-Native Company',
    learningOutcome: 'Which loop to build first, what earns autonomy next, what breaks.',
    order: 59,
  },
  'ai-agent-roi': {
    group: 'Production',
    displayTitle: 'AI Agent ROI: The Fleet Audit',
    learningOutcome: 'The landed-not-produced audit: how to tell a working fleet from activity theater, with our own failure receipts.',
    order: 59.1,
  },
  'reviewing-ai-generated-pull-requests': {
    group: 'Production',
    displayTitle: 'Reviewing AI-Generated Pull Requests',
    learningOutcome: 'The review packet, the AI_POLICY.md, and the three machine gates that run before a human sees the diff.',
    order: 60,
  },
  'ai-agent-runaway-cost': {
    group: 'Production',
    displayTitle: 'Why Your Agent Bill Is Wrong',
    learningOutcome: 'The cost categories session metrics leave out, a script that refuses to print a partial total, and the file that stops recursive fan-out.',
    order: 61,
  },
  'agent-tool-permissions-canary': {
    group: 'Production',
    displayTitle: 'A Role Label Is Not a Sandbox',
    learningOutcome: 'A two-phase canary suite that proves a deny rule actually denies, why denying one command leaves the other routes open, and how to read a tool grant for what is missing.',
    order: 62,
  },
  'who-owns-your-ai-agents': {
    group: 'Production',
    displayTitle: 'Your Agents Have Production Credentials and No Owner',
    learningOutcome: 'A fillable agent registry, an offboarding runbook whose third step makes you watch the old credential fail, an append-only action-log contract with correlation IDs, and the autonomy ladder as a table with demotion triggers.',
    order: 63,
  },
  'ai-agent-seo-loop': {
    group: 'Production',
    displayTitle: 'How to Build an SEO Agent Loop',
    learningOutcome: 'The four loop roles search actually needs, the emerging-term wedge with our own Search Console numbers, and the rank-monitoring rule that scores the daily series, reads the incomplete-data boundary the API response carries, and refuses to produce a verdict from a trailing average.',
    order: 64,
  },
  'ai-agent-social-loop': {
    group: 'Production',
    displayTitle: 'How to Cold-Launch a Social Presence With Agent Loops',
    learningOutcome: 'Seven social loops on one fleet, four of which reach a live account without a human in the moment, what each has measurably earned, the three ways a posting loop goes quiet while reporting ok, and the account-level cadence rule that takes the slot atomically instead of reading a ledger that cannot be read in time.',
    order: 65,
  },

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║  AI CODING TOOLS > Cursor & Tools                               ║
  // ╚══════════════════════════════════════════════════════════════════╝
  'cursor-ai-ship-code-faster': {
    group: 'Cursor & Tools',
    displayTitle: 'Ship Code 10x Faster with Cursor',
    learningOutcome: 'Complete Cursor IDE setup - Tab, Cmd+K, Chat, Composer, .cursorrules.',
    order: 1,
  },
  '5-levels-of-coding-agents': {
    group: 'Cursor & Tools',
    displayTitle: 'The 5 Levels of Coding Agent Autonomy',
    learningOutcome: 'From simple code review to fully autonomous engineering.',
    order: 2,
  },
  'google-antigravity-complete-guide': {
    group: 'Cursor & Tools',
    displayTitle: "Google Antigravity: Complete Guide",
    learningOutcome: "Complete guide to Google's free agent IDE.",
    order: 3,
  },
  'codex-cli-guide-2026': {
    group: 'Cursor & Tools',
    displayTitle: 'Codex CLI: How It Works + When to Use It',
    learningOutcome: 'The auth/billing fork, the 2026 model decision tree, and Codex vs Claude Code.',
    order: 4,
  },
}

// ── Cross-listed lessons ─────────────────────────────────────────────
// A post lives canonically in ONE course (via blogGroupMap) but can appear
// as a lesson in another course's outline. One article, no duplication.
export const crossListedLessons: Record<
  CurriculumKey,
  Record<string, BlogGroupEntry>
> = {
  'claude-code': {
    'context-engineering-guide': {
      group: 'Getting Started',
      displayTitle: '.agent Folder: Context Engineering',
      learningOutcome: 'Context engineering with a structured .agent documentation system. Shared lesson with the AI Agents course.',
      order: 6,
    },
    'codex-cli-guide-2026': {
      group: 'Workflows & Techniques',
      displayTitle: '/goals Command: Tips & Mistakes',
      learningOutcome: 'Goal-driven agent sessions - tips and mistakes. Shared lesson with the AI Coding Tools course.',
      order: 18,
    },
    'mcp-101-build-mcp-servers': {
      group: 'Workflows & Techniques',
      displayTitle: 'Connect MCP Servers to Claude Code',
      learningOutcome: 'Extend Claude Code with custom tools via MCP. Shared lesson with the AI Agents course.',
      order: 25,
    },
  },
  'ai-agents': {
    'agent-skills-best-practices-guide': {
      group: 'Tools & Infrastructure',
      displayTitle: 'Why MCP is Dead & Skills Replace It',
      learningOutcome: 'Where Skills replace MCP servers - and where they don\'t. Shared lesson with the Claude Code course.',
      order: 33,
    },
  },
  'ai-coding-tools': {},
}

// ── Standalone guides (not in curriculum, shown as card rows) ────────
// Every YouTube video lives embedded inside a host article (post body), so
// listings only ever reference posts. Posts with an embed get the red "Video"
// pill via post.hasVideo (derived in getAllBlogPosts).
export interface StandaloneCategory {
  label: string
  postIds: string[]
}

export const standaloneCategories: StandaloneCategory[] = [
  {
    label: 'Comparisons',
    postIds: [
      'claude-code-vs-cursor-2026',
      'opencode-vs-claude-code-2026',
      'gemini-35-flash-vs-claude-sonnet-2026',
      'langchain-vs-crewai-vs-raw-api',
      'best-ai-coding-agent-2026',
      'best-ai-agent-courses-2026',
      'best-claude-code-courses-2026',
    ],
  },
  {
    label: 'Agent Skills Library',
    postIds: [
      'claude-code-for-sales-skills-guide',
      'claude-code-for-data-scientists-skills-guide',
      'claude-code-for-growth-marketing-skills-guide',
      'claude-code-for-devops-skills-guide',
      'claude-code-for-solopreneurs-skills-guide',
    ],
  },
  {
    label: 'Claude Code for Your Role',
    postIds: [
      'claude-code-for-startup-founders',
      'claude-code-for-data-engineers',
      'claude-code-for-devops',
      'claude-code-for-frontend-developers',
      'claude-code-for-api-development',
      'claude-code-for-sales-teams',
      'claude-code-for-freelancers',
      'build-saas-claude-code-tutorial',
    ],
  },
  {
    label: 'UI & Design',
    postIds: [
      'vibe-design-ai-ui-workflow',
    ],
  },
  {
    label: 'Builder Stories & Guides',
    postIds: [
      'garry-tan-ai-coding-workflow',
      'vibe-coding-non-engineers',
      'zero-to-saas-30-days',
      'gstack-complete-course',
      'ai-content-pipeline-guide',
      'ai-proof-your-career-2027',
      'ai-data-analysis-no-code',
      'gemini-3-power-guide',
    ],
  },
  {
    label: 'News & Updates',
    postIds: [
      'google-io-2026-developer-recap',
      'google-kills-gemini-cli-june-18-2026',
      'what-is-claude-tag',
    ],
  },
  {
    label: 'Tool Reviews',
    postIds: [
      'lovable-ai-review-2026',
    ],
  },
]

// ── Legacy compatibility: topics array for tab rendering ────────────
// (Kept for backward compat with search filtering)
export interface TopicTab {
  key: CurriculumKey | 'all'
  label: string
  matchTags: string[]
}

export const topics: TopicTab[] = [
  { key: 'all', label: 'All', matchTags: [] },
  { key: 'claude-code', label: 'Claude Code', matchTags: ['claude-code', 'claude-md'] },
  { key: 'ai-agents', label: 'AI Agents', matchTags: ['ai-agents', 'agents', 'mcp'] },
  { key: 'ai-coding-tools', label: 'AI Coding Tools', matchTags: ['cursor', 'ai-coding', 'ide', 'google-antigravity'] },
]

export const groupOrder = curriculumGroupOrder

// ── Course navigation context (sidebar + prev/next on article pages) ─

export interface CourseLesson {
  id: string
  number: string
  displayTitle: string
  href: string
  isVideo: boolean
  isExternal: boolean
}

export interface CourseGroup {
  name: string
  lessons: CourseLesson[]
}

export interface CourseContext {
  sectionKey: CurriculumKey
  sectionLabel: string
  groups: CourseGroup[]
  current: CourseLesson
  prev: CourseLesson | null
  next: CourseLesson | null
}

/**
 * Returns the course outline + prev/next for a curriculum article, or null
 * for posts outside the curriculum (standalone guides, redirects, etc).
 * videoPostIds: ids of posts whose body embeds a video - their lessons get
 * the "Video" pill (derive it from getAllBlogPosts' post.hasVideo).
 */
export function getCourseContext(
  postId: string,
  videoPostIds: Set<string>,
): CourseContext | null {
  const meta = blogGroupMap[postId]
  if (!meta) return null

  const section = curriculumSections.find((s) =>
    curriculumGroupOrder[s.key].includes(meta.group),
  )
  if (!section) return null

  const groups: CourseGroup[] = curriculumGroupOrder[section.key].map(
    (groupName, gIdx) => {
      const entries: { id: string; displayTitle: string; order: number }[] = []

      Object.entries(blogGroupMap).forEach(([id, m]) => {
        if (m.group === groupName) {
          entries.push({ id, displayTitle: m.displayTitle, order: m.order })
        }
      })
      Object.entries(crossListedLessons[section.key] ?? {}).forEach(([id, m]) => {
        if (m.group === groupName) {
          entries.push({ id, displayTitle: m.displayTitle, order: m.order })
        }
      })

      entries.sort((a, b) => a.order - b.order)

      return {
        name: groupName,
        lessons: entries.map((e, i) => ({
          id: e.id,
          number: `${gIdx + 1}.${i + 1}`,
          displayTitle: e.displayTitle,
          href: `/blog/${e.id}`,
          isVideo: videoPostIds.has(e.id),
          isExternal: false,
        })),
      }
    },
  )

  const flat = groups.flatMap((g) => g.lessons)
  const currentIndex = flat.findIndex((l) => l.id === postId)
  if (currentIndex === -1) return null

  return {
    sectionKey: section.key,
    sectionLabel: section.label,
    groups,
    current: flat[currentIndex],
    prev: currentIndex > 0 ? flat[currentIndex - 1] : null,
    next: currentIndex < flat.length - 1 ? flat[currentIndex + 1] : null,
  }
}
